import { Authenticated, Refine } from "@refinedev/core";
import { DevtoolsPanel, DevtoolsProvider } from "@refinedev/devtools";
import { RefineKbar, RefineKbarProvider } from "@refinedev/kbar";

import {
  ErrorComponent,
  RefineSnackbarProvider,
  ThemedLayout,
  useNotificationProvider,
} from "@refinedev/mui";

import AutoAwesomeOutlined from "@mui/icons-material/AutoAwesomeOutlined";
import GavelOutlined from "@mui/icons-material/GavelOutlined";
import PlayCircleOutline from "@mui/icons-material/PlayCircleOutline";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import CssBaseline from "@mui/material/CssBaseline";
import GlobalStyles from "@mui/material/GlobalStyles";
import routerProvider, {
  CatchAllNavigate,
  DocumentTitleHandler,
  NavigateToResource,
  UnsavedChangesNotifier,
} from "@refinedev/react-router";
import { BrowserRouter, Outlet, Route, Routes } from "react-router";
import { Header, Sider } from "./components";
import { ColorModeContextProvider } from "./contexts/color-mode";
import { DashboardPage } from "@pages/dashboard";
import {
  UserCreate,
  UserEdit,
  UserList,
  UserShow,
} from "@pages/users";
import {
  CreditBundleCreate,
  CreditBundleEdit,
  CreditBundleList,
  CreditBundleShow,
} from "@pages/credit-bundles";
import {
  ChallengeCreate,
  ChallengeEdit,
  ChallengeList,
  ChallengeShow,
} from "@pages/challenges";
import {
  FeatureFlagCreate,
  FeatureFlagEdit,
  FeatureFlagList,
  FeatureFlagShow,
} from "@pages/feature-flags";
import { StoryGenerationList } from "@pages/story-generations";
import { CreationList, CreationShow } from "@pages/creations";
import { LibraryList } from "@pages/library";
import { StoryViewList } from "@pages/story-views";
import { StoryCommentList } from "@pages/story-comments";
import { ContentReportList } from "@pages/content-reports";
import { RemovalRequestList } from "@pages/removal-requests";
import { AccountDeletionList } from "@pages/account-deletions";
import { EvidenceHoldList, EvidenceHoldShow } from "@pages/evidence";
import { ComplianceList } from "@pages/compliance";
import { CharacterList } from "@pages/characters";
import { GalleryList } from "@pages/gallery";
import { LoginPage } from "@pages/login";
import { authProvider } from "@/authProvider";
import { dataProvider } from "./providers/data";
import { accessControlProvider } from "./providers/access-control";
import { EVIDENCE_RESOURCE } from "@/lib/evidence";
import { ACCOUNT_DELETIONS_RESOURCE } from "@/lib/account-deletion";
import {Title} from "@/components/title";

function App() {
  return (
    <BrowserRouter>
      <RefineKbarProvider>
        <ColorModeContextProvider>
          <CssBaseline />
          <GlobalStyles styles={{ html: { WebkitFontSmoothing: "auto" } }} />
          <RefineSnackbarProvider>
            <DevtoolsProvider>
              <Refine
                dataProvider={dataProvider('/api/v2')}
                authProvider={authProvider}
                accessControlProvider={accessControlProvider}
                notificationProvider={useNotificationProvider}
                routerProvider={routerProvider}
                resources={[
                  {
                    // A menu section, not an API resource: no routes, so the
                    // sider draws it as a header over the resources naming it
                    // as their `parent`.
                    name: "section/general",
                    meta: { label: "General", icon: <SettingsOutlined /> },
                  },
                  {
                    name: "user",
                    list: "/users",
                    create: "/users/create",
                    edit: "/users/edit/:id",
                    show: "/users/show/:id",
                    meta: {
                      parent: "section/general",
                      canDelete: true,
                    },
                  },
                  {
                    name: "credit-bundles",
                    list: "/credit-bundles",
                    create: "/credit-bundles/create",
                    edit: "/credit-bundles/edit/:id",
                    show: "/credit-bundles/show/:id",
                    meta: {
                      parent: "section/general",
                      // The v2 gem shop's packs (immersive-server credit_bundles).
                      label: "Credits Packs",
                      canDelete: true,
                    },
                  },
                  {
                    // `name` is the API resource (→ /api/v2/config/flags); routes
                    // live under /config/flags to avoid any name/route ambiguity.
                    name: "config/flags",
                    list: "/config/flags",
                    create: "/config/flags/create",
                    edit: "/config/flags/edit/:id",
                    show: "/config/flags/show/:id",
                    meta: {
                      parent: "section/general",
                      label: "Feature Flags",
                      canDelete: true,
                    },
                  },
                  {
                    name: "challenges",
                    list: "/challenges",
                    create: "/challenges/create",
                    edit: "/challenges/edit/:id",
                    show: "/challenges/show/:id",
                    meta: {
                      parent: "section/general",
                      canDelete: true,
                    },
                  },
                  {
                    name: "section/play",
                    meta: { label: "Play", icon: <PlayCircleOutline /> },
                  },
                  {
                    // `name` is the API resource (→ /api/v2/admin/library);
                    // list + delete only (no create/edit/show routes).
                    name: "admin/library",
                    list: "/library",
                    meta: {
                      parent: "section/play",
                      label: "Library",
                      canDelete: true,
                    },
                  },
                  {
                    // `name` is the API resource (→ /api/v2/admin/story-views);
                    // read-only aggregate counters — no create/edit/show routes
                    // and no delete (pruning counters is an operator script).
                    name: "admin/story-views",
                    list: "/story-views",
                    meta: {
                      parent: "section/play",
                      label: "Story Views",
                      canDelete: false,
                    },
                  },
                  {
                    // `name` is the API resource (→ /api/v2/admin/story-comments);
                    // list + delete only — moderation removes a comment, it
                    // never rewrites one, so there is no edit/show route.
                    name: "admin/story-comments",
                    list: "/story-comments",
                    meta: {
                      parent: "section/play",
                      label: "Story Comments",
                      canDelete: true,
                    },
                  },
                  {
                    // `name` is the API resource (→ /api/v2/admin/gallery);
                    // list + delete. Deleting takes the piece out of that
                    // player's Gallery on every client; the file stays.
                    name: "admin/gallery",
                    list: "/gallery",
                    meta: {
                      parent: "section/play",
                      label: "Gallery",
                      canDelete: true,
                    },
                  },
                  {
                    name: "section/create",
                    meta: { label: "Create", icon: <AutoAwesomeOutlined /> },
                  },
                  {
                    // `name` is the API resource (→ /api/v2/admin/creations);
                    // v1 stories and Story Studio items (by `kind`). Edit is a
                    // modal on the list; show is the row whole, its JSON.
                    name: "admin/creations",
                    list: "/creations",
                    show: "/creations/show/:id",
                    meta: {
                      parent: "section/create",
                      label: "Creations",
                      canDelete: true,
                    },
                  },
                  {
                    // `name` is the API resource (→ /api/v2/admin/characters);
                    // read-only — a library character is one creator's own
                    // working material rather than published content, so there
                    // is nothing here to moderate.
                    name: "admin/characters",
                    list: "/characters",
                    meta: {
                      parent: "section/create",
                      label: "Characters",
                      canDelete: false,
                    },
                  },
                  {
                    // `name` is the API resource (→ /api/v2/admin/generations);
                    // the Story Studio's jobs (studio_jobs). Read-only: no create,
                    // edit, show or delete.
                    name: "admin/generations",
                    list: "/story-generations",
                    meta: {
                      parent: "section/create",
                      label: "Story Generations",
                      canDelete: false,
                    },
                  },
                  {
                    name: "section/compliance",
                    meta: { label: "Compliance", icon: <GavelOutlined />, defaultCollapsed: true },
                  },
                  {
                    // `name` is the API resource (→ /api/v2/admin/compliance);
                    // read-only — the disclosure, crisis and consent ledgers are
                    // append-only evidence, and the server exposes no write verb
                    // on them. One page, several artefacts, no edit/show routes.
                    name: "admin/compliance",
                    list: "/compliance",
                    meta: {
                      parent: "section/compliance",
                      label: "Compliance",
                      canDelete: false,
                    },
                  },
                  {
                    // `name` is the API resource (→ /api/v2/admin/content-reports);
                    // List only — no `edit` route, because there is no edit page:
                    // the status change happens inline in the grid. Declaring one
                    // would have Refine offer edit affordances that route nowhere.
                    // Closing a ticket never removes the reported content either.
                    // A v1 target is acted on through its own resource; a v2 one
                    // through the row's own actions (remove a comment, take an
                    // entry down), each kept in the report's history.
                    name: "admin/content-reports",
                    list: "/content-reports",
                    meta: {
                      parent: "section/compliance",
                      label: "Reports",
                      canDelete: false,
                    },
                  },
                  {
                    // `name` is the API resource (→ /api/v2/admin/removal-requests);
                    // List only — triage happens in a drawer on the list page, so
                    // there is no edit/show route to declare. Never deletable: every
                    // row and its log is the compliance record for a statutory
                    // notice, and there is no delete route on the server either.
                    name: "admin/removal-requests",
                    list: "/removal-requests",
                    meta: {
                      parent: "section/compliance",
                      label: "Removal Requests",
                      canDelete: false,
                    },
                  },
                  {
                    // `name` is the API resource (→ /api/v2/admin/account-deletions).
                    // Read-only: the jobs a user delete schedules. A finished or
                    // failed job can outlive the user row it named, and then this
                    // is the only place it shows.
                    name: ACCOUNT_DELETIONS_RESOURCE,
                    list: "/account-deletions",
                    meta: {
                      parent: "section/compliance",
                      label: "Account Deletions",
                      canDelete: false,
                    },
                  },
                  {
                    // `name` is the API resource (→ /api/v2/admin/evidence/holds).
                    // Evidence holds for apparent CSAM (immersive-server
                    // plans/compliance-launch/ncmec.md). The menu entry exists only
                    // for an admin on the server's reviewer list — the access-control
                    // provider asks `GET admin/evidence/access`. `show` is
                    // `/evidence/:id` because that is the link every hold alert
                    // carries. Never deletable: release is the only way a hold's
                    // copies go, and it is a reviewer's decision on the hold's page.
                    name: EVIDENCE_RESOURCE,
                    list: "/evidence",
                    show: "/evidence/:id",
                    meta: {
                      parent: "section/compliance",
                      label: "Evidence",
                      canDelete: false,
                    },
                  },
                ]}
                options={{
                  syncWithLocation: true,
                  warnWhenUnsavedChanges: true,
                  projectId: "mvHfNx-XBS8pR-LP6Ygr",
                }}
              >
                <Routes>
                  <Route
                    element={
                      <Authenticated key="authenticated-routes" fallback={<CatchAllNavigate to="/login" />}>
                        <ThemedLayout
                          Header={() => <Header sticky />}
                          Title={Title}
                          Sider={Sider}
                        >
                          <Outlet />
                        </ThemedLayout>
                      </Authenticated>
                    }
                  >
                    <Route
                      index
                      element={<DashboardPage/>}
                    />
                    <Route path="/users">
                      <Route index element={<UserList />} />
                      <Route path="create" element={<UserCreate />} />
                      <Route path="edit/:id" element={<UserEdit />} />
                      <Route path="show/:id" element={<UserShow />} />
                    </Route>
                    <Route path="/account-deletions">
                      <Route index element={<AccountDeletionList />} />
                    </Route>
                    <Route path="/credit-bundles">
                      <Route index element={<CreditBundleList />} />
                      <Route path="create" element={<CreditBundleCreate />} />
                      <Route path="edit/:id" element={<CreditBundleEdit />} />
                      <Route path="show/:id" element={<CreditBundleShow />} />
                    </Route>
                    <Route path="/challenges">
                      <Route index element={<ChallengeList />} />
                      <Route path="create" element={<ChallengeCreate />} />
                      <Route path="edit/:id" element={<ChallengeEdit />} />
                      <Route path="show/:id" element={<ChallengeShow />} />
                    </Route>
                    <Route path="/config/flags">
                      <Route index element={<FeatureFlagList />} />
                      <Route path="create" element={<FeatureFlagCreate />} />
                      <Route path="edit/:id" element={<FeatureFlagEdit />} />
                      <Route path="show/:id" element={<FeatureFlagShow />} />
                    </Route>
                    <Route path="/story-generations">
                      <Route index element={<StoryGenerationList />} />
                    </Route>
                    <Route path="/creations">
                      <Route index element={<CreationList />} />
                      <Route path="show/:id" element={<CreationShow />} />
                    </Route>
                    <Route path="/library">
                      <Route index element={<LibraryList />} />
                    </Route>
                    <Route path="/story-views">
                      <Route index element={<StoryViewList />} />
                    </Route>
                    <Route path="/story-comments">
                      <Route index element={<StoryCommentList />} />
                    </Route>
                    <Route path="/content-reports">
                      <Route index element={<ContentReportList />} />
                    </Route>
                    <Route path="/removal-requests">
                      <Route index element={<RemovalRequestList />} />
                    </Route>
                    <Route path="/compliance">
                      <Route index element={<ComplianceList />} />
                    </Route>
                    <Route path="/evidence">
                      <Route index element={<EvidenceHoldList />} />
                      <Route path=":id" element={<EvidenceHoldShow />} />
                    </Route>
                    <Route path="/characters">
                      <Route index element={<CharacterList />} />
                    </Route>
                    <Route path="/gallery">
                      <Route index element={<GalleryList />} />
                    </Route>
                    <Route path="*" element={<ErrorComponent />} />
                  </Route>

                  <Route
                    element={
                      <Authenticated key="auth-pages" fallback={<Outlet />}>
                        <NavigateToResource resource="user" />
                      </Authenticated>
                    }
                  >
                    <Route path="/login" element={<LoginPage />} />
                  </Route>
                </Routes>

                <RefineKbar />
                <UnsavedChangesNotifier />
                <DocumentTitleHandler
                  handler={({ autoGeneratedTitle }) =>
                    autoGeneratedTitle
                      ? `Immersive Admin Panel | ${autoGeneratedTitle}`
                      : "Immersive Admin Panel"
                  }
                />
              </Refine>
              <DevtoolsPanel />
            </DevtoolsProvider>
          </RefineSnackbarProvider>
        </ColorModeContextProvider>
      </RefineKbarProvider>
    </BrowserRouter>
  );
}

export default App;
